import "./EventsPast.scss";

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdEdit, MdContentCopy } from "react-icons/md";
import { toast } from "react-toastify";

import TableColumn from "../TableColumn/TableColumn";
import api from "../../services/api";
import parseDate from "../../utils/parseDate";
import Skeleton from "../Skeleton/Skeleton";

// Copy-friendly Event ID cell: shows a truncated id but copies the FULL
// Mongo _id so it can be pasted into the Manual Pushes deep-link field.
const EventIdCell = ({ eventId }) => {
    if (!eventId) return "—";
    const short =
        eventId.length > 12
            ? `${eventId.slice(0, 8)}…${eventId.slice(-4)}`
            : eventId;
    return (
        <span
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard?.writeText(eventId);
                toast.success("Event ID copied");
            }}
            title={`${eventId}\n(click to copy)`}
            style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12,
                color: "rgba(255,255,255,0.6)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
            }}
        >
            {short}
            <MdContentCopy size={14} />
        </span>
    );
};

const EventsPast = () => {
    const navigate = useNavigate();

    const sorts = ["Name", "Category", "Music"];
    const headings = [
        "Image",
        "Name",
        "Event ID",
        "Date",
        "Event hours",
        "Saved",
        "Review Guestlist",
        "Review Ticket sales",
        "Bottle Service",
    ];
    const [events, setEvents] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");

    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(
        Number(localStorage.getItem("ListCurrentPageEventsPast")) || 1
    );
    const [isSearching, setIsSearching] = useState(false);
    const records = 10;

    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState("null");

    function formatTime(minutes) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const ampm = hours >= 12 ? "PM" : "AM";
        const hour12 = hours % 12 || 12;
        const minuteFormatted = mins.toString().padStart(2, "0");
        return `${hour12}:${minuteFormatted} ${ampm}`;
    }

    useEffect(() => {
        const fetchData = async (page, searchTerm) => {
            setIsSearching(true);

            try {
                const response = await api.get(`/events/past`, {
                    params: {
                        name: searchTerm,
                        page: page,
                        records: records,
                        // sort_column: sortBy,
                        // sort: sortDirection,
                        offset: new Date().getTimezoneOffset(),
                    },
                });

                const formattedEvents = response.data.data.map((item) => {
                    const { from, to } = item.hours;


                    return {
                        id: item._id,
                        Image: (
                            <img
                                className="rounded"
                                style={{ height: 40, width: 40, objectFit: "cover" }}
                                src={item.image_urls?.[0] || ""}
                                alt={item.name}
                            />
                        ),
                        Venue: item.venue_name,
                        Name: item.name,
                        "Event ID": <EventIdCell eventId={item._id} />,
                        Category: item.tags.map((tag) => tag).join(", "),
                        Location: item.location,
                        'Date': parseDate(item.date),
                        Dress: item.dress,
                        Music: item.music.map((music) => music).join(", "),
                        Address: item.address,
                        About:
                            item?.description?.length > 50
                                ? `${item.description.slice(0, 50)}...`
                                : item.description,
                        Saved: item.number_of_saves,
                        "Guestlist": item.check_ins,


                        "Event hours": `${formatTime(from)} - ${formatTime(to)}`,
                        "Review Guestlist": (
                            <Link
                                to={`/events/review-guestlist/${item._id}`}
                                style={{ color: "#4F30EA" }}
                            >
                                {item.guest_list_count}
                            </Link>
                        ),
                        "Review Ticket sales": (
                            <Link
                                to={`/events/review-tickets/${item._id}`}
                                style={{ color: "#4F30EA" }}
                            >
                                {item.ticket_sales_count}
                            </Link>
                        ),
                        "Bottle Service": (
                            <Link
                                to={`/events/review-bottle-service/${item._id}`}
                                style={{ color: "#4F30EA" }}
                            >
                                {item.paidUsers || 0}
                            </Link>
                        ),
                    }
                });
                setTotalPages(response.data.total);
                setEvents(formattedEvents);
            } catch (error) {
                console.log(`events get request didn't work. error: ${error}`);
            } finally {
                setIsSearching(false);
            }
        };
        fetchData(currentPage, searchTerm)
    }, [sortBy, sortDirection, currentPage, searchTerm])

    const actions = [
        {
            label: <MdEdit size={30} color="#4F30EA" />,
            onClick: (rowData) => {
                navigate(`/events/edit/${rowData.id}`);
            },
        },
    ];

    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm]);

    const handleChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        localStorage.setItem("ListCurrentPageEventsPast", page);
    };



    const fetchAllData = async () => {
        try {
            const response = await api.get(`/events/past`, {
                params: {
                    records: 9999999999,
                },
            });


            return response.data.data.map((item) => ({
                id: item._id,
                Venue: item.venue_name,
                Name: item.name,
                Category: item.tags.map((tag) => tag).join(", "),
                Location: item.location,
                'Date': parseDate(item.date),
                Dress: item.dress,
                Music: item.music.map((music) => music).join(", "),
                Address: item.address,
                About:
                    item?.description?.length > 50
                        ? `${item.description.slice(0, 50)}...`
                        : item.description,
                Saved: item.number_of_saves,
                "Guestlist": item.check_ins,
            }));

        } catch (error) {
            console.log(`Error fetching all data: ${error}`);
            return [];
        }
    };


    useEffect(() => {
        const storedPage = localStorage.getItem("ListCurrentPageEventsPast");

        setCurrentPage(Number(storedPage) || 1);
    }, []);


    const sortColumnsMapping = {
        Name: "name",
        Category: "tags",
        Music: "music",
    };

    const handleSort = (columnName) => {
        const backendColumnName = sortColumnsMapping[columnName];
        if (sortBy === backendColumnName) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(backendColumnName);
            setSortDirection("asc");
        }
    };

    return (
        isSearching ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}><Skeleton type="row" /><Skeleton type="row" /><Skeleton type="row" /><Skeleton type="row" /></div>
        ) : (
            <TableColumn
                hasHeader={true}
                columns={headings}
                data={events}
                sortableColumns={sorts}
                hasExport={true}
                hasSearch={true}
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                records={records}
                actions={actions}
                inputSearchProps={{ value: searchTerm, onChange: handleChange }}
                fetchAllData={fetchAllData}
                onSort={handleSort}
                hasModifyHeader={true}
            />
        )
    );
}

export default EventsPast;
